import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Button } from "../components/ui/button";
import { PageHeader, SectionCard, Loader, ConfirmDelete, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { UserDialog } from "../components/UserDialog";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useDepot } from "../context/DepotContext";
import { api, formatApiError } from "../lib/api";

export default function Users() {
  const { user: me } = useAuth();
  const { depotName } = useDepot();
  const { data, reload } = useApi("/users");
  const [dialog, setDialog] = useState({ open: false, user: null });
  const pendingCount = (data || []).filter((u) => u.pending).length;

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); reload(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Owner" title="Users" subtitle="Create employee accounts and lock each one to a depot. Admins see every depot."
        actions={
          <Button onClick={() => setDialog({ open: true, user: null })} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="add-user-button">
            <Plus className="h-4 w-4" /> New user
          </Button>
        } />
      {pendingCount > 0 && (
        <div className="rise rounded-lg border border-amber-500/40 bg-amber-500/10 px-5 py-3 text-sm text-amber-200" data-testid="pending-requests-banner">
          {pendingCount} account request{pendingCount > 1 ? "s" : ""} waiting. Click <b>Review</b>, assign a depot and save to approve — or delete to reject.
        </div>
      )}
      <SectionCard title={`${data?.length ?? 0} accounts`}>
        {!data ? <Loader /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className={TABLE_HEAD}>
                <tr>
                  <th className="px-4 py-2.5 text-left">Name</th><th className="px-4 py-2.5 text-left">Role</th>
                  <th className="px-4 py-2.5 text-left">Depot</th><th className="px-4 py-2.5 text-left">Status</th><th className="px-2" />
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <tr key={u.id} className={TABLE_ROW} data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-2.5"><div className="font-medium">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                    <td className="px-4 py-2.5">
                      <span className={`font-mono text-[10px] uppercase tracking-wider rounded border px-2 py-0.5 ${u.role === "admin" ? "border-amber-500/30 text-amber-400" : "border-slate-700 text-slate-300"}`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-300">{u.role === "admin" ? "All depots" : u.role === "truck_manager" ? "Trucks (Dar es Salaam)" : u.depot_id ? depotName(u.depot_id) : "Not assigned"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs ${u.pending ? "text-amber-400" : u.active ? "text-emerald-400" : "text-rose-400"}`} data-testid={`user-status-${u.email}`}>
                        {u.pending ? "Pending approval" : u.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 whitespace-nowrap text-right">
                      {u.pending && (
                        <Button size="sm" onClick={() => setDialog({ open: true, user: u })} className="mr-1 bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid={`approve-user-${u.email}`}>
                          Review
                        </Button>
                      )}
                      <button onClick={() => setDialog({ open: true, user: u })} className="rounded p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-100" data-testid={`edit-user-${u.email}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      {u.id !== me.id && <ConfirmDelete title={`Delete ${u.name}?`} description="They will no longer be able to sign in. Their past records stay." onConfirm={() => remove(u.id)} testid={`delete-user-${u.email}`} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
      <UserDialog open={dialog.open} user={dialog.user} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={reload} />
    </div>
  );
}
